const CELL_SIZE = 20
import {
  PLAYER_SPRITES as BASE_PLAYER_SPRITES,
  PLAYER_WALK_SPRITES as BASE_PLAYER_WALK_SPRITES,
  ENEMY_SPRITES as BASE_ENEMY_SPRITES,
  ENEMY_DIR_SPRITES as BASE_ENEMY_DIR_SPRITES,
  ENEMY_WALK_SPRITES as BASE_ENEMY_WALK_SPRITES
} from './Sprites.js'

const DIRECTION_ROW = {
  down: 0,
  left: 1,
  right: 2,
  up: 3
}

function frameFor(path, direction, column, fallback = null) {
  const row = DIRECTION_ROW[direction] ?? 0
  return {
    imageUrl: path,
    frameX: column * CELL_SIZE,
    frameY: row * CELL_SIZE,
    frameWidth: CELL_SIZE,
    frameHeight: CELL_SIZE,
    width: CELL_SIZE,
    height: CELL_SIZE,
    fallback
  }
}

function directionalSet(path, fallbackDirectional) {
  return {
    down: frameFor(path, 'down', 0, fallbackDirectional.down),
    left: frameFor(path, 'left', 0, fallbackDirectional.left),
    right: frameFor(path, 'right', 0, fallbackDirectional.right),
    up: frameFor(path, 'up', 0, fallbackDirectional.up)
  }
}

function walkSet(path, fallbackWalk) {
  return {
    down: [
      frameFor(path, 'down', 0, fallbackWalk.down[0]),
      frameFor(path, 'down', 1, fallbackWalk.down[1]),
      frameFor(path, 'down', 2, fallbackWalk.down[2]),
      frameFor(path, 'down', 3, fallbackWalk.down[3])
    ],
    left: [
      frameFor(path, 'left', 0, fallbackWalk.left[0]),
      frameFor(path, 'left', 1, fallbackWalk.left[1]),
      frameFor(path, 'left', 2, fallbackWalk.left[2]),
      frameFor(path, 'left', 3, fallbackWalk.left[3])
    ],
    right: [
      frameFor(path, 'right', 0, fallbackWalk.right[0]),
      frameFor(path, 'right', 1, fallbackWalk.right[1]),
      frameFor(path, 'right', 2, fallbackWalk.right[2]),
      frameFor(path, 'right', 3, fallbackWalk.right[3])
    ],
    up: [
      frameFor(path, 'up', 0, fallbackWalk.up[0]),
      frameFor(path, 'up', 1, fallbackWalk.up[1]),
      frameFor(path, 'up', 2, fallbackWalk.up[2]),
      frameFor(path, 'up', 3, fallbackWalk.up[3])
    ]
  }
}

const CHARACTER_SHEET_PATHS = {
  player: '/assets/sprites/characters/player.png',
  shopkeeperForest: '/assets/sprites/characters/shopkeeper-forest.png',
  shopkeeperGraveyard: '/assets/sprites/characters/shopkeeper-graveyard.png',
  shopkeeperCaves: '/assets/sprites/characters/shopkeeper-caves.png',
  shopkeeperEmberkeep: '/assets/sprites/characters/shopkeeper-emberkeep.png',
  shopkeeperPurpledooms: '/assets/sprites/characters/shopkeeper-purpledooms.png',
  villager1: '/assets/sprites/characters/villager-1.png',
  villager2: '/assets/sprites/characters/villager-2.png',
  villager3: '/assets/sprites/characters/villager-3.png',
  villager4: '/assets/sprites/characters/villager-4.png',
  villager5: '/assets/sprites/characters/villager-5.png',
  zombie: '/assets/sprites/characters/zombie.png',
  skeleton: '/assets/sprites/characters/skeleton.png',
  spider: '/assets/sprites/characters/spider.png',
  orc: '/assets/sprites/characters/orc.png',
  darkKnight: '/assets/sprites/characters/darkKnight.png',
  shadowWolf: '/assets/sprites/characters/shadowWolf.png',
  darkWizard: '/assets/sprites/characters/darkWizard.png',
  darkKing: '/assets/sprites/characters/darkKing.png'
}

export const PLAYER_SPRITES = directionalSet(CHARACTER_SHEET_PATHS.player, BASE_PLAYER_SPRITES)
export const PLAYER_WALK_SPRITES = walkSet(CHARACTER_SHEET_PATHS.player, BASE_PLAYER_WALK_SPRITES)
export const SHOPKEEPER_SPRITES = {
  forest: frameFor(CHARACTER_SHEET_PATHS.shopkeeperForest, 'down', 0, BASE_PLAYER_SPRITES.down),
  graveyard: frameFor(CHARACTER_SHEET_PATHS.shopkeeperGraveyard, 'down', 0, BASE_PLAYER_SPRITES.down),
  caves: frameFor(CHARACTER_SHEET_PATHS.shopkeeperCaves, 'down', 0, BASE_PLAYER_SPRITES.down),
  emberkeep: frameFor(CHARACTER_SHEET_PATHS.shopkeeperEmberkeep, 'down', 0, BASE_PLAYER_SPRITES.down),
  purpledooms: frameFor(CHARACTER_SHEET_PATHS.shopkeeperPurpledooms, 'down', 0, BASE_PLAYER_SPRITES.down)
}

export const VILLAGER_SPRITE_SETS = [
  CHARACTER_SHEET_PATHS.villager1,
  CHARACTER_SHEET_PATHS.villager2,
  CHARACTER_SHEET_PATHS.villager3,
  CHARACTER_SHEET_PATHS.villager4,
  CHARACTER_SHEET_PATHS.villager5
].map(path => ({
  standing: directionalSet(path, BASE_PLAYER_SPRITES),
  walking: walkSet(path, BASE_PLAYER_WALK_SPRITES)
}))

export const ENEMY_SPRITES = {
  ...BASE_ENEMY_SPRITES,
  ...Object.fromEntries(
    Object.entries(CHARACTER_SHEET_PATHS)
      .filter(([name]) => (
        name !== 'player' &&
        !name.startsWith('shopkeeper') &&
        !name.startsWith('villager')
      ))
      .map(([name, path]) => [name, frameFor(path, 'down', 0, BASE_ENEMY_SPRITES[name])])
  )
}

export const ENEMY_DIR_SPRITES = Object.fromEntries(
  Object.entries(ENEMY_SPRITES).map(([name]) => {
    const fallbackDirectional = BASE_ENEMY_DIR_SPRITES[name] || {
      down: BASE_ENEMY_SPRITES[name],
      left: BASE_ENEMY_SPRITES[name],
      right: BASE_ENEMY_SPRITES[name],
      up: BASE_ENEMY_SPRITES[name]
    }
    const path = CHARACTER_SHEET_PATHS[name]
    return [name, path ? directionalSet(path, fallbackDirectional) : fallbackDirectional]
  })
)

export const ENEMY_WALK_SPRITES = Object.fromEntries(
  Object.entries(ENEMY_SPRITES).map(([name]) => {
    const fallbackDirectional = BASE_ENEMY_DIR_SPRITES[name] || {
      down: BASE_ENEMY_SPRITES[name],
      left: BASE_ENEMY_SPRITES[name],
      right: BASE_ENEMY_SPRITES[name],
      up: BASE_ENEMY_SPRITES[name]
    }
    const fallbackWalk = BASE_ENEMY_WALK_SPRITES[name] || {
      down: [fallbackDirectional.down, fallbackDirectional.down, fallbackDirectional.down, fallbackDirectional.down],
      left: [fallbackDirectional.left, fallbackDirectional.left, fallbackDirectional.left, fallbackDirectional.left],
      right: [fallbackDirectional.right, fallbackDirectional.right, fallbackDirectional.right, fallbackDirectional.right],
      up: [fallbackDirectional.up, fallbackDirectional.up, fallbackDirectional.up, fallbackDirectional.up]
    }
    const path = CHARACTER_SHEET_PATHS[name]
    return [name, path ? walkSet(path, fallbackWalk) : fallbackWalk]
  })
)
